<template>
  <AppHeaderBack title="Update University Information" />
  <div class="min-h-screen bg-blue-50">
    <!-- Header section with UP colors -->
    <div class="bg-gradient-to-r from-blue-900 to-blue-700 py-8 shadow-md">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex flex-col items-center">
          <div class="text-center">
            <h1 class="text-2xl font-bold text-white mb-1">Update University Information</h1>
            <p class="text-blue-100">Keep your university details up to date</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Main content -->
    <div class="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-6">
      <div class="grid grid-cols-1">
        <div class="bg-white rounded-lg shadow transform transition-transform duration-300 hover:-translate-y-1 hover:shadow-md">
          <div class="p-6">
            <form @submit.prevent="updateUniversity" class="space-y-6">
              <!-- Responsive grid for form fields -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <!-- University Name Dropdown -->
                <div>
                  <label for="university" class="block text-sm font-medium text-gray-700 mb-1">University</label>
                  <div class="relative">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span class="material-symbols-outlined text-gray-400">account_balance</span>
                    </div>
                    <select
                      id="university"
                      v-model="selectedUniversity"
                      required
                      :disabled="loading"
                      class="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-900 focus:border-blue-900 text-gray-700 transition duration-200 disabled:opacity-50 bg-white appearance-none"
                      @change="handleUniversityChange"
                    >
                      <option value="" disabled selected>Select university</option>
                      <option v-for="(uni, key) in universityOptions" :key="key" :value="key">{{ uni.name }}</option>
                    </select>
                    <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <span class="material-symbols-outlined text-gray-400">arrow_drop_down</span>
                    </div>
                  </div>
                </div>

                <!-- Faculty Dropdown -->
                <div>
                  <label for="faculty" class="block text-sm font-medium text-gray-700 mb-1">Faculty</label>
                  <div class="relative">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span class="material-symbols-outlined text-gray-400">business</span>
                    </div>
                    <select
                      id="faculty"
                      v-model="university.faculty"
                      required
                      :disabled="loading || !selectedUniversity"
                      class="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-900 focus:border-blue-900 text-gray-700 transition duration-200 disabled:opacity-50 bg-white appearance-none"
                    >
                      <option value="" disabled selected>Select faculty</option>
                      <option v-for="(faculty, index) in availableFaculties" :key="index" :value="faculty">{{ faculty }}</option>
                    </select>
                    <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <span class="material-symbols-outlined text-gray-400">arrow_drop_down</span>
                    </div>
                  </div>
                </div>

                <!-- Course Name Dropdown -->
                <div>
                  <label for="courseName" class="block text-sm font-medium text-gray-700 mb-1">
                    Course Name <span class="text-xs text-gray-400">(optional)</span>
                  </label>
                  <div class="relative">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span class="material-symbols-outlined text-gray-400">menu_book</span>
                    </div>
                    <select
                      id="courseName"
                      v-model="university.courseName"
                      :disabled="loading || !university.faculty"
                      class="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-900 focus:border-blue-900 text-gray-700 transition duration-200 disabled:opacity-50 bg-white appearance-none"
                      @change="handleCourseNameChange"
                    >
                      <option value="">Select course</option>
                      <option v-for="(program, key) in availablePrograms" :key="key" :value="program">{{ program }}</option>
                    </select>
                    <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <span class="material-symbols-outlined text-gray-400">arrow_drop_down</span>
                    </div>
                  </div>
                </div>

                <!-- Study Year Dropdown (Optional) -->
                <div>
                  <label for="studyYear" class="block text-sm font-medium text-gray-700 mb-1">
                    Study Year <span class="text-xs text-gray-400">(optional)</span>
                  </label>
                  <div class="relative">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span class="material-symbols-outlined text-gray-400">calendar_month</span>
                    </div>
                    <select
                      id="studyYear"
                      v-model="university.studyYear"
                      :disabled="loading"
                      class="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-900 focus:border-blue-900 text-gray-700 transition duration-200 disabled:opacity-50 bg-white appearance-none"
                    >
                      <option value="" selected>Select study year</option>
                      <option v-for="year in studyYearOptions" :key="year" :value="year">{{ year }}</option>
                    </select>
                    <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <span class="material-symbols-outlined text-gray-400">arrow_drop_down</span>
                    </div>
                  </div>
                </div>

                <!-- Level of Study Dropdown -->
                <div>
                  <label for="levelOfStudy" class="block text-sm font-medium text-gray-700 mb-1">Level of Study</label>
                  <div class="relative">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span class="material-symbols-outlined text-gray-400">stairs</span>
                    </div>
                    <select
                      id="levelOfStudy"
                      v-model="university.levelOfStudy"
                      required
                      :disabled="loading"
                      class="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-900 focus:border-blue-900 text-gray-700 transition duration-200 disabled:opacity-50 bg-white appearance-none"
                    >
                      <option value="" disabled selected>Select level of study</option>
                      <option v-for="(level, index) in levelOfStudyOptions" :key="index" :value="level">{{ level }}</option>
                    </select>
                    <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <span class="material-symbols-outlined text-gray-400">arrow_drop_down</span>
                    </div>
                  </div>
                </div>

                <!-- Target Credits Input -->
                <div>
                  <label for="targetCredits" class="block text-sm font-medium text-gray-700 mb-1">
                    Target Credits <span class="text-xs text-gray-400">(optional)</span>
                  </label>
                  <div class="relative">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span class="material-symbols-outlined text-gray-400">flag</span>
                    </div>
                    <input
                      id="targetCredits"
                      v-model="university.targetCredits"
                      type="number"
                      min="1"
                      step="1"
                      inputmode="numeric"
                      placeholder="e.g. 120"
                      :disabled="loading"
                      class="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-900 focus:border-blue-900 text-gray-700 transition duration-200 disabled:opacity-50"
                      :class="{ 'border-red-400 focus:border-red-500 focus:ring-red-200': !isTargetCreditsValid }"
                    />
                  </div>
                  <p class="mt-1 text-xs" :class="isTargetCreditsValid ? 'text-gray-500' : 'text-red-600'">
                    {{ isTargetCreditsValid ? 'Used by Credits Tracker to calculate your progress.' : 'Enter a number greater than 0, or leave it blank.' }}
                  </p>
                </div>

                <!-- Campus Multi-Select (full width on desktop) -->
                <div class="md:col-span-2">
                  <label for="campus" class="block text-sm font-medium text-gray-700 mb-1">Campus (Select all that apply)</label>
                  <div class="relative border border-gray-300 rounded-lg p-3 focus-within:ring-2 focus-within:ring-blue-900 focus-within:border-blue-900">
                    <div class="mb-2 flex flex-wrap gap-2">
                      <div 
                        v-for="(campus, idx) in university.campuses" 
                        :key="idx"
                        class="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-sm flex items-center"
                      >
                        <span>{{ campus }}</span>
                        <button 
                          type="button"
                          @click="removeCampus(campus)"
                          class="ml-1 text-blue-600 hover:text-blue-800"
                          :disabled="loading"
                        >
                          <span class="material-symbols-outlined text-xs">close</span>
                        </button>
                      </div>
                      <div v-if="university.campuses.length === 0" class="text-gray-400 text-sm italic">
                        No campuses selected
                      </div>
                    </div>

                    <div class="max-h-48 overflow-auto border-t border-gray-200 pt-2">
                      <div class="space-y-2">
                        <label 
                          v-for="(campus, idx) in campusOptions" 
                          :key="idx" 
                          class="flex items-center hover:bg-blue-50 p-1 rounded cursor-pointer"
                        >
                          <input 
                            type="checkbox" 
                            :value="campus" 
                            :checked="isCampusSelected(campus)"
                            @change="toggleCampus(campus)"
                            class="mr-2 h-4 w-4 text-blue-900 rounded focus:ring-blue-500"
                          />
                          <span>{{ campus }}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Modules Selection (full width) -->
              <div class="mt-6">
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  Modules <span class="text-xs text-gray-400">(Select modules you are taking)</span>
                </label>
                
                <!-- Module filter options -->
                <div class="mb-3 flex flex-wrap gap-2">
                  <!-- Faculty filter -->
                  <div class="flex-1 min-w-[200px]">
                    <select
                      v-model="selectedModuleFaculty"
                      class="w-full p-2 border border-gray-300 rounded-md text-sm"
                      @change="filterModulesByFaculty"
                    >
                      <option value="">All Faculties</option>
                      <option v-for="faculty in availableFaculties" :key="faculty" :value="faculty">
                        {{ faculty }}
                      </option>
                    </select>
                  </div>
                  
                  <!-- Program filter -->
                  <div class="flex-1 min-w-[200px]" v-if="selectedModuleFaculty && curricula && curricula[selectedModuleFaculty]">
                    <select
                      v-model="selectedProgram"
                      class="w-full p-2 border border-gray-300 rounded-md text-sm"
                      @change="selectProgram(selectedProgram)"
                    >
                      <option value="">All Programs</option>
                      <option 
                        v-for="(program, key) in curricula[selectedModuleFaculty].Undergraduate" 
                        :key="key" 
                        :value="key"
                      >
                        {{ program.programmeName }}
                      </option>
                    </select>
                  </div>
                  
                  <!-- Study level filter -->
                  <div class="flex-1 min-w-[200px]">
                    <select
                      v-model="selectedLevel"
                      class="w-full p-2 border border-gray-300 rounded-md text-sm"
                      @change="selectedLevel ? selectStudyLevel(selectedLevel) : loadAllModules()"
                    >
                      <option value="">All Study Levels</option>
                      <option value="1">First Year</option>
                      <option value="2">Second Year</option>
                      <option value="3">Third Year</option>
                      <option value="FIN">Final Year</option>
                    </select>
                  </div>
                </div>

                <div class="relative border border-gray-300 rounded-lg p-3 focus-within:ring-2 focus-within:ring-blue-900 focus-within:border-blue-900">
                  <!-- Selected modules -->
                  <div class="mb-2 flex flex-wrap gap-2">
                    <div 
                      v-for="(module, idx) in university.modules" 
                      :key="idx"
                      class="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-sm flex items-center"
                    >
                      <span>{{ module }}</span>
                      <button 
                        type="button"
                        @click="removeModule(module)"
                        class="ml-1 text-blue-600 hover:text-blue-800"
                        :disabled="loading"
                      >
                        <span class="material-symbols-outlined text-xs">close</span>
                      </button>
                    </div>
                    <div v-if="!university.modules || university.modules.length === 0" class="text-gray-400 text-sm italic">
                      No modules selected
                    </div>
                  </div>
                  
                  <!-- Search box -->
                  <div class="relative mb-2">
                    <input
                      v-model="moduleSearch"
                      type="text"
                      placeholder="Search for modules..."
                      class="w-full p-2 border border-gray-300 rounded-md"
                      @input="filterModules"
                    />
                  </div>

                  <!-- Module list -->
                  <div class="max-h-48 overflow-auto border-t border-gray-200 pt-2">
                    <div v-if="loading" class="text-center py-2">
                      <div class="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                      <p class="text-sm text-gray-500 mt-1">Loading modules...</p>
                    </div>
                    <div v-else-if="filteredModules.length === 0" class="text-center py-2 text-gray-500">
                      No modules found matching your search
                    </div>
                    <div v-else class="space-y-2">
                      <label 
                        v-for="module in filteredModules" 
                        :key="module"
                        class="flex items-center hover:bg-blue-50 p-1 rounded cursor-pointer"
                      >
                        <input 
                          type="checkbox" 
                          :value="module" 
                          :checked="isModuleSelected(module)"
                          @change="toggleModule(module)"
                          class="mr-2 h-4 w-4 text-blue-900 rounded focus:ring-blue-500"
                        />
                        <span>{{ module }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Submit and Cancel Buttons -->
              <div class="flex flex-col gap-4">
                <!-- Submit Button -->
                <button
                  type="submit"
                  :disabled="loading || !isFormValid"
                  class="w-full flex justify-center items-center py-3 px-4 rounded-lg font-semibold text-white bg-gradient-to-r from-blue-900 to-blue-700 hover:from-blue-800 hover:to-blue-600 transition disabled:opacity-60"
                >
                  <svg v-if="loading" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {{ loading ? "Updating..." : "Save Changes" }}
                </button>
                
                <!-- Cancel Button -->
                <router-link 
                  to="/profile"
                  class="w-full flex justify-center items-center py-3 px-4 rounded-lg font-semibold text-blue-900 border border-blue-900 bg-white hover:bg-blue-50 transition"
                >
                  Cancel
                </router-link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppHeaderBack from '@/components/common/AppHeaderBack.vue';
import { mapState, mapMutations } from 'vuex';
import { updateUserUniversity, getUserProfile } from '@/composables/useFirestore';
import { normalizeModuleKey } from '@/utils/creditEstimator';

export default {
  components: {
    AppHeaderBack,
  },
  data() {
    return {
      selectedUniversity: '',
      university: {
        name: '',
        studentNumber: '',
        faculty: '',
        courseName: '',
        studyYear: '',
        levelOfStudy: '',
        targetCredits: '',
        campuses: [],
        modules: [],
        moduleCredits: {}
      },
      moduleSearch: '',
      filteredModules: [],
      availableModules: [],
      selectedProgram: '',
      selectedLevel: '',
      selectedModuleFaculty: '', // for filter, but default is ''
      allModules: [], // store all modules from all faculties
      syncingProfile: false
    };
  },
  computed: {
    ...mapState(['user', 'profile', 'loading', 'globalSettings', 'curricula']),
    universityOptions() {
      return this.globalSettings?.universities || {};
    },
    availableFaculties() {
      // Get faculties directly from curricula data instead of globalSettings
      if (!this.curricula) return [];
      return Object.keys(this.curricula);
    },
    facultyOptions() {
      // This is maintained for backward compatibility but not used for faculty dropdown
      if (!this.selectedUniversity) return [];
      const universityData = this.universityOptions[this.selectedUniversity];
      return universityData?.faculties || [];
    },
    campusOptions() {
      if (!this.selectedUniversity) return [];
      const universityData = this.universityOptions[this.selectedUniversity];
      return universityData?.campuses || [];
    },
    levelOfStudyOptions() {
      return this.globalSettings?.levelsOfStudy || [];
    },
    studyYearOptions() {
      return [1, 2, 3, 4, 5, 6, 7];
    },
    isFormValid() {
      return (
        this.university.name && 
        this.university.faculty && 
        this.university.levelOfStudy &&
        this.isTargetCreditsValid &&
        this.university.campuses.length > 0 // Check if at least one campus is selected
      );
    },
    isTargetCreditsValid() {
      if (this.university.targetCredits === '' || this.university.targetCredits === null || this.university.targetCredits === undefined) {
        return true;
      }

      const targetCredits = Number(this.university.targetCredits);
      return Number.isFinite(targetCredits) && targetCredits > 0;
    },
    availablePrograms() {
      if (!this.curricula || !this.university.faculty || !this.curricula[this.university.faculty]) {
        return [];
      }
      
      const facultyData = this.curricula[this.university.faculty];
      if (!facultyData.Undergraduate) {
        return [];
      }
      
      // Extract program names from the curricula structure
      const programs = Object.values(facultyData.Undergraduate)
        .map(program => program.programmeName)
        .filter(Boolean); // Remove any undefined values
      
      return programs.sort(); // Sort alphabetically
    }
  },
  watch: {
    profile: {
      handler(newProfile) {
        if (newProfile && newProfile.university) {
          this.syncingProfile = true;
          this.university = {
            name: newProfile.university.name || '',
            studentNumber: newProfile.university.studentNumber || '',
            faculty: newProfile.university.faculty || '',
            courseName: newProfile.university.courseName || '', // new field
            studyYear: newProfile.university.studyYear || '',   // new field
            levelOfStudy: newProfile.university.levelOfStudy || '',
            targetCredits: newProfile.university.targetCredits ?? '',
            campuses: Array.isArray(newProfile.university.campuses) 
              ? [...newProfile.university.campuses] 
              : (newProfile.university.campus ? [newProfile.university.campus] : []),
            modules: Array.isArray(newProfile.university.modules)
              ? [...newProfile.university.modules]
              : [],
            moduleCredits: newProfile.university.moduleCredits && typeof newProfile.university.moduleCredits === 'object'
              ? { ...newProfile.university.moduleCredits }
              : {}
          };
          
          // Match university name to key in universities object
          if (this.university.name && this.globalSettings?.universities) {
            for (const [key, uni] of Object.entries(this.globalSettings.universities)) {
              if (uni.name === this.university.name) {
                this.selectedUniversity = key;
                break;
              }
            }
          }
          
          // Check if the faculty from profile is in our curricula data
          if (this.university.faculty && this.curricula) {
            if (!this.availableFaculties.includes(this.university.faculty)) {
              console.warn(`[UserUpdateUniversity] Faculty from profile "${this.university.faculty}" not found in curricula data`);
              
              // Try to map EBIT to a proper faculty name from curricula
              if (this.university.faculty === 'EBIT' || 
                  this.university.faculty === 'Faculty of Engineering, Built Environment and Information Technology') {
                const match = this.availableFaculties.find(f => 
                  f.includes('Engineering') || 
                  f.includes('Information Technology') || 
                  f.includes('Built Environment')
                );
                
                if (match) {
                  console.log(`[UserUpdateUniversity] Mapped "${this.university.faculty}" to "${match}"`);
                  this.university.faculty = match;
                }
              }
            }
          }
          
          // Initialize available modules if faculty is already selected
          if (this.university.faculty) {
            this.loadAvailableModules();
          }

          this.$nextTick(() => {
            this.syncingProfile = false;
          });
        }
      },
      immediate: true
    },
    'university.faculty': function(newFaculty) {
      if (newFaculty) {
        console.log(`[UserUpdateUniversity] Faculty changed to: ${newFaculty}`);

        if (this.syncingProfile) {
          this.loadAvailableModules();
          return;
        }

        // Reset program and level selections when faculty changes
        this.selectedProgram = '';
        this.selectedLevel = '';
        // Reset the course name when faculty changes
        this.university.courseName = '';
        this.university.modules = []; // Clear selected modules when faculty changes
        this.university.moduleCredits = {};
        this.loadAvailableModules();
      } else {
        this.availableModules = [];
        this.filteredModules = [];
      }
    },
    // When selected program changes, update the course name if it's empty
    selectedProgram: function(newProgram) {
      if (newProgram && this.curricula && this.university.faculty && this.curricula[this.university.faculty]?.Undergraduate?.[newProgram]) {
        const programName = this.curricula[this.university.faculty].Undergraduate[newProgram].programmeName;
        // Only update course name if it's empty or if user confirms the change
        if (!this.university.courseName || confirm(`Would you like to update your course name to "${programName}"?`)) {
          this.university.courseName = programName;
        }
      }
    },
    curricula: {
      handler(newCurricula) {
        if (newCurricula && this.university.faculty) {
          console.log('[UserUpdateUniversity] Curricula data loaded, checking faculty compatibility');
          
          // Check if current faculty exists in curricula
          if (!Object.keys(newCurricula).includes(this.university.faculty)) {
            console.warn(`[UserUpdateUniversity] Current faculty not found in curricula: ${this.university.faculty}`);
            
            // Try to map EBIT to a proper faculty name from curricula
            if (this.university.faculty === 'EBIT' || 
                this.university.faculty === 'Faculty of Engineering, Built Environment and Information Technology') {
              const match = Object.keys(newCurricula).find(f => 
                f.includes('Engineering') || 
                f.includes('Information Technology') || 
                f.includes('Built Environment')
              );
              
              if (match) {
                console.log(`[UserUpdateUniversity] Mapped "${this.university.faculty}" to "${match}"`);
                this.university.faculty = match;
              }
            }
          }
          
          // Try to load modules with potentially updated faculty
          this.loadAvailableModules();
        }
      },
      deep: true
    }
  },
  mounted() {
    // Make sure curricula data is loaded and load all modules for all faculties by default
    if (!this.curricula) {
      this.$store.dispatch('fetchCurriculaData').then(() => {
        this.loadAllModules();
      });
    } else {
      this.loadAllModules();
    }
  },
  methods: {
    ...mapMutations(['setLoading', 'setProfile']),
    handleUniversityChange() {
      if (this.selectedUniversity) {
        const universityData = this.universityOptions[this.selectedUniversity];
        this.university.name = universityData.name;
        this.university.faculty = '';
        this.university.campuses = [];
        this.university.courseName = ''; // reset on university change
        this.university.studyYear = '';  // reset on university change
        this.university.targetCredits = '';
        this.university.modules = []; // reset modules on university change
        this.university.moduleCredits = {};
        this.selectedProgram = ''; // Reset selected program
        this.selectedLevel = ''; // Reset selected level
        this.availableModules = []; // Clear available modules
        this.filteredModules = []; // Clear filtered modules
      }
    },
    toggleCampus(campus) {
      if (this.isCampusSelected(campus)) {
        this.removeCampus(campus);
      } else {
        this.addCampus(campus);
      }
    },
    isCampusSelected(campus) {
      return this.university.campuses.includes(campus);
    },
    addCampus(campus) {
      if (!this.university.campuses.includes(campus)) {
        this.university.campuses.push(campus);
      }
    },
    removeCampus(campus) {
      this.university.campuses = this.university.campuses.filter(c => c !== campus);
    },
    // Module-related methods
    loadAvailableModules() {
      this.availableModules = [];
      if (!this.curricula || !this.university.faculty) return;
      
      console.log('[UserUpdateUniversity] Loading modules for faculty:', this.university.faculty);
      
      // Try to find modules for this faculty in the curricula data
      const facultyData = this.curricula[this.university.faculty];
      if (facultyData && facultyData.Undergraduate) {
        console.log('[UserUpdateUniversity] Faculty data structure found with Undergraduate programs');
        
        // Get all modules from all programs and levels
        const allModules = new Set();
        
        // Iterate through all programs in the faculty
        Object.keys(facultyData.Undergraduate).forEach(programKey => {
          const program = facultyData.Undergraduate[programKey];
          console.log(`[UserUpdateUniversity] Processing program: ${program.programmeName}`);
          
          // Check if the program has modulesByLevel
          if (program.modulesByLevel) {
            // Add modules from each level
            Object.keys(program.modulesByLevel).forEach(level => {
              const modulesForLevel = program.modulesByLevel[level];
              if (Array.isArray(modulesForLevel)) {
                modulesForLevel.forEach(module => allModules.add(module));
              }
            });
          }
        });
        
        // Convert the Set back to an array and sort
        this.availableModules = Array.from(allModules).sort();
        console.log(`[UserUpdateUniversity] Loaded ${this.availableModules.length} unique modules from all programs`);
      } else {
        console.warn('[UserUpdateUniversity] No Undergraduate data found for faculty:', this.university.faculty);
        // Create some default modules based on faculty name since we couldn't find data
        this.availableModules = [
          `${this.university.faculty} 101`,
          `${this.university.faculty} 102`,
          `${this.university.faculty} 201`,
          `${this.university.faculty} 301`,
          `Research Methods in ${this.university.faculty}`
        ];
        console.log('[UserUpdateUniversity] Created default modules');
      }
      
      this.filterModules();
    },
    
    // Add methods to filter by program and study level
    loadAllModules() {
      // Loads all modules from all faculties, regardless of selected faculty
      if (!this.curricula) return;
      const allModules = new Set();
      Object.keys(this.curricula).forEach(faculty => {
        const facultyData = this.curricula[faculty];
        if (facultyData && facultyData.Undergraduate) {
          Object.values(facultyData.Undergraduate).forEach(program => {
            if (program.modulesByLevel) {
              Object.values(program.modulesByLevel).forEach(modulesArr => {
                if (Array.isArray(modulesArr)) {
                  modulesArr.forEach(module => allModules.add(module));
                }
              });
            }
          });
        }
      });
      this.availableModules = Array.from(allModules).sort();
      this.allModules = this.availableModules;
      this.filterModules();
    },
    filterModulesByFaculty() {
      // If no faculty selected, show all modules
      if (!this.selectedModuleFaculty) {
        this.loadAllModules();
        return;
      }
      // Otherwise, filter modules by selected faculty
      const facultyData = this.curricula?.[this.selectedModuleFaculty];
      const facultyModules = new Set();
      if (facultyData && facultyData.Undergraduate) {
        Object.values(facultyData.Undergraduate).forEach(program => {
          if (program.modulesByLevel) {
            Object.values(program.modulesByLevel).forEach(modulesArr => {
              if (Array.isArray(modulesArr)) {
                modulesArr.forEach(module => facultyModules.add(module));
              }
            });
          }
        });
      }
      this.availableModules = Array.from(facultyModules).sort();
      this.filterModules();
    },
    selectProgram(programKey) {
      // Only filter by program if a faculty is selected
      if (!this.curricula || !this.selectedModuleFaculty) return;
      const facultyData = this.curricula[this.selectedModuleFaculty];
      if (facultyData?.Undergraduate?.[programKey]?.modulesByLevel) {
        const program = facultyData.Undergraduate[programKey];
        const programModules = new Set();
        Object.values(program.modulesByLevel).forEach(modulesArr => {
          if (Array.isArray(modulesArr)) {
            modulesArr.forEach(module => programModules.add(module));
          }
        });
        this.availableModules = Array.from(programModules).sort();
        this.filterModules();
      }
    },
    selectStudyLevel(level) {
      // If filtering by faculty, filter by level within that faculty, else show all modules for that level
      if (this.selectedModuleFaculty) {
        const facultyData = this.curricula?.[this.selectedModuleFaculty];
        const levelModules = new Set();
        if (facultyData && facultyData.Undergraduate) {
          Object.values(facultyData.Undergraduate).forEach(program => {
            if (program.modulesByLevel && program.modulesByLevel[level]) {
              const modulesArr = program.modulesByLevel[level];
              if (Array.isArray(modulesArr)) {
                modulesArr.forEach(module => levelModules.add(module));
              }
            }
          });
        }
        this.availableModules = Array.from(levelModules).sort();
        this.filterModules();
      } else {
        // All faculties: filter all modules by level
        const levelModules = new Set();
        Object.keys(this.curricula).forEach(faculty => {
          const facultyData = this.curricula[faculty];
          if (facultyData && facultyData.Undergraduate) {
            Object.values(facultyData.Undergraduate).forEach(program => {
              if (program.modulesByLevel && program.modulesByLevel[level]) {
                const modulesArr = program.modulesByLevel[level];
                if (Array.isArray(modulesArr)) {
                  modulesArr.forEach(module => levelModules.add(module));
                }
              }
            });
          }
        });
        this.availableModules = Array.from(levelModules).sort();
        this.filterModules();
      }
    },
    filterModules() {
      if (!this.moduleSearch) {
        this.filteredModules = [...this.availableModules];
        return;
      }
      const search = this.moduleSearch.toLowerCase();
      this.filteredModules = this.availableModules.filter(module =>
        module.toLowerCase().includes(search)
      );
    },
    toggleModule(module) {
      if (this.isModuleSelected(module)) {
        this.removeModule(module);
      } else {
        this.addModule(module);
      }
    },
    isModuleSelected(module) {
      return this.university.modules.includes(module);
    },
    addModule(module) {
      if (!this.university.modules) {
        this.university.modules = [];
      }
      if (!this.university.modules.includes(module)) {
        this.university.modules.push(module);
      }
    },
    removeModule(module) {
      const moduleKey = normalizeModuleKey(module);
      this.university.modules = this.university.modules.filter(m => m !== module);
      if (moduleKey && this.university.moduleCredits) {
        const nextModuleCredits = { ...this.university.moduleCredits };
        delete nextModuleCredits[moduleKey];
        this.university.moduleCredits = nextModuleCredits;
      }
    },
    async updateUniversity() {
      if (!this.university.name || !this.university.faculty || !this.university.levelOfStudy) {
        this.$store.dispatch('showAlert', { 
          message: "Please select a university, faculty, and level of study", 
          type: "error" 
        });
        return;
      }

      if (this.university.campuses.length === 0) {
        this.$store.dispatch('showAlert', { 
          message: "Please select at least one campus", 
          type: "error" 
        });
        return;
      }

      if (!this.isTargetCreditsValid) {
        this.$store.dispatch('showAlert', { 
          message: "Please enter target credits greater than 0, or leave it blank.", 
          type: "error" 
        });
        return;
      }

      this.setLoading(true);
      try {
        const universityToSave = { ...this.university };

        if (universityToSave.targetCredits === '' || universityToSave.targetCredits === null || universityToSave.targetCredits === undefined) {
          delete universityToSave.targetCredits;
        } else {
          universityToSave.targetCredits = Number(universityToSave.targetCredits);
        }

        const selectedModuleKeys = new Set(
          (universityToSave.modules || []).map(module => normalizeModuleKey(module)).filter(Boolean)
        );
        const cleanedModuleCredits = {};

        Object.entries(universityToSave.moduleCredits || {}).forEach(([moduleKey, credits]) => {
          const normalizedKey = normalizeModuleKey(moduleKey);
          const creditValue = Number(credits);

          if (selectedModuleKeys.has(normalizedKey) && Number.isFinite(creditValue) && creditValue >= 0) {
            cleanedModuleCredits[normalizedKey] = creditValue;
          }
        });

        if (Object.keys(cleanedModuleCredits).length > 0) {
          universityToSave.moduleCredits = cleanedModuleCredits;
        } else {
          delete universityToSave.moduleCredits;
        }

        await updateUserUniversity(this.user.uid, universityToSave);
        const updatedProfile = await getUserProfile(this.user.uid);
        this.setProfile(updatedProfile);

        this.$store.dispatch('showAlert', { 
          message: "University information updated successfully!", 
          type: "success" 
        });
        this.$router.push('/profile');
      } catch (error) {
        this.$store.dispatch('showAlert', { 
          message: error.message || "Failed to update university info", 
          type: "error" 
        });
      } finally {
        this.setLoading(false);
      }
    }
  }
};
</script>

<style scoped>
/* Custom scrollbar for the campus options */
.overflow-auto::-webkit-scrollbar {
  width: 8px;
}

.overflow-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.overflow-auto::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 4px;
}

.overflow-auto::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>

